import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Plus, Search, MoreHorizontal, Eye, Edit, FileText, Calendar, Globe, Send, Users, UserPlus, Copy } from 'lucide-react';
import { toast } from 'sonner';
import QuickAssignModal from '../components/QuickAssignModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new_request: { label: 'New Request', class: 'bg-blue-100 text-blue-800' },
  needs_info: { label: 'Needs Info', class: 'bg-yellow-100 text-yellow-800' },
  quoted: { label: 'Quoted', class: 'bg-purple-100 text-purple-800' },
  confirmed: { label: 'Confirmed', class: 'bg-green-100 text-green-800' },
  assigned: { label: 'Assigned', class: 'bg-teal-100 text-teal-800' },
  in_progress: { label: 'In Progress', class: 'bg-orange-100 text-orange-800' },
  delivered: { label: 'Delivered', class: 'bg-emerald-100 text-emerald-800' },
  vendor_invoice_received: { label: 'Vendor Invoice', class: 'bg-indigo-100 text-indigo-800' },
  client_invoiced: { label: 'Client Invoiced', class: 'bg-cyan-100 text-cyan-800' },
  vendor_paid: { label: 'Vendor Paid', class: 'bg-lime-100 text-lime-800' },
  client_paid: { label: 'Client Paid', class: 'bg-green-100 text-green-800' },
  closed: { label: 'Closed', class: 'bg-gray-100 text-gray-800' },
  cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800' }
};

const WorkOrders = () => {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Broadcast Dialog State
  const [showBroadcast, setShowBroadcast] = useState(false);
  const [broadcastWO, setBroadcastWO] = useState(null);
  const [matchingVendors, setMatchingVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [inviteAll, setInviteAll] = useState(true);

  // Quick Assign Modal State
  const [quickAssignOpen, setQuickAssignOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);

  // Duplicate Work Order State
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateWorkOrder, setDuplicateWorkOrder] = useState(null);
  const [duplicateDate, setDuplicateDate] = useState('');
  const [duplicateTime, setDuplicateTime] = useState('');
  const [duplicateLoading, setDuplicateLoading] = useState(false);

  const handleQuickAssign = (wo) => {
    setSelectedWorkOrder(wo);
    setQuickAssignOpen(true);
  };

  const handleDuplicateClick = (wo) => {
    setDuplicateWorkOrder(wo);
    // Pre-fill with tomorrow's date
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setDuplicateDate(tomorrow.toISOString().split('T')[0]);
    // Pre-fill time from original work order if available
    const originalTime = wo.interpreting_details?.start_time || wo.scheduled_time || '';
    setDuplicateTime(originalTime ? originalTime.substring(0, 5) : '09:00');
    setDuplicateDialogOpen(true);
  };

  const handleDuplicateSubmit = async () => {
    if (!duplicateDate) {
      toast.error('Please select a date');
      return;
    }
    
    setDuplicateLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/work-orders/${duplicateWorkOrder.work_order_id}/duplicate`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          new_date: duplicateDate,
          new_time: duplicateTime
        })
      });
      
      if (response.ok) {
        const newWO = await response.json();
        toast.success(`Work order duplicated! New ID: ${newWO.work_order_id}`);
        setDuplicateDialogOpen(false);
        fetchWorkOrders(); // Refresh list
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to duplicate work order');
      }
    } catch (error) {
      toast.error('Failed to duplicate work order');
    } finally {
      setDuplicateLoading(false);
    }
  };

  const fetchWorkOrders = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('work_order_type', typeFilter);
      
      const response = await fetch(`${API_URL}/api/work-orders?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWorkOrders(data);
      }
    } catch (error) {
      toast.error('Failed to load work orders');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
  }, [search, statusFilter, typeFilter]);

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openBroadcastDialog = async (wo) => {
    setBroadcastWO(wo);
    setShowBroadcast(true);
    setBroadcastLoading(true);
    setSelectedVendors([]);
    setInviteAll(true);
    setBroadcastMessage('');
    
    try {
      // Build query params for smart matching
      const params = new URLSearchParams({
        work_order_type: wo.work_order_type
      });
      if (wo.service_category) params.append('service_category', wo.service_category);
      if (wo.service_subcategory) params.append('service_subcategory', wo.service_subcategory);
      if (wo.languages?.length > 0) params.append('language', wo.languages[0]);
      if (wo.interpreting_details?.modality) params.append('modality', wo.interpreting_details.modality);
      
      const response = await fetch(`${API_URL}/api/vendors/match?${params}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMatchingVendors(data.vendors || []);
      }
    } catch (error) {
      toast.error('Failed to load matching vendors');
    } finally {
      setBroadcastLoading(false);
    }
  };

  const handleBroadcast = async () => {
    setBroadcastLoading(true);
    
    try {
      const payload = {
        work_order_id: broadcastWO.work_order_id,
        vendor_ids: inviteAll ? null : selectedVendors,
        message: broadcastMessage || null
      };
      
      const response = await fetch(`${API_URL}/api/broadcast/send`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        setShowBroadcast(false);
        fetchWorkOrders();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send broadcast');
      }
    } catch (error) {
      toast.error('Failed to send broadcast');
    } finally {
      setBroadcastLoading(false);
    }
  };

  const toggleVendorSelection = (vendorId) => {
    setSelectedVendors(prev => 
      prev.includes(vendorId) 
        ? prev.filter(id => id !== vendorId)
        : [...prev, vendorId]
    );
  };

  return (
    <div className="space-y-6" data-testid="work-orders-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search work orders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="wo-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="wo-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <SelectItem key={key} value={key}>{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[180px]" data-testid="wo-type-filter">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="interpreting">Interpreting</SelectItem>
              <SelectItem value="translation">Translation</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Link to="/work-orders/new">
          <Button className="gap-2" data-testid="create-wo-btn">
            <Plus className="h-4 w-4" />
            New Work Order
          </Button>
        </Link>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : workOrders.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No work orders found</h3>
              <p className="text-muted-foreground mb-4">Create your first work order to get started</p>
              <Link to="/work-orders/new">
                <Button>Create Work Order</Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Languages</TableHead>
                  <TableHead>Schedule/Deadline</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Margin</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {workOrders.map((wo) => (
                  <TableRow key={wo.work_order_id} data-testid={`wo-row-${wo.work_order_id}`}>
                    <TableCell>
                      <div 
                        className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        onClick={() => navigate(`/work-orders/${wo.work_order_id}`)}
                      >
                        {wo.work_order_id}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {wo.work_order_type === 'interpreting' ? (
                          <Calendar className="h-4 w-4 text-primary" />
                        ) : (
                          <Globe className="h-4 w-4 text-secondary" />
                        )}
                        <span className="capitalize">{wo.work_order_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div 
                        className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        onClick={() => wo.client_id && navigate(`/clients/${wo.client_id}`)}
                      >
                        {wo.client_name || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {wo.languages?.map((lang) => (
                          <Badge key={lang} variant="outline" className="text-xs">
                            {lang.toUpperCase()}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {wo.work_order_type === 'interpreting' 
                        ? formatDate(wo.interpreting_details?.start_datetime)
                        : formatDate(wo.deadline)}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(wo.status)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={wo.margin >= 0 ? 'text-green-600' : 'text-red-600'}>
                        ${wo.margin?.toFixed(2) || '0.00'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Quick Assign Button - Show for confirmed and unassigned work orders */}
                        {wo.status === 'confirmed' && !wo.assigned_vendor_id && (
                          <Button 
                            variant="outline" 
                            size="icon"
                            className="h-8 w-8 border-blue-300 text-blue-600 hover:bg-blue-50"
                            onClick={() => handleQuickAssign(wo)}
                            title="Quick Assign"
                            data-testid={`quick-assign-${wo.work_order_id}`}
                          >
                            <UserPlus className="h-4 w-4" />
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`wo-actions-${wo.work_order_id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/work-orders/${wo.work_order_id}`)}>
                              <Eye className="h-4 w-4 mr-2" /> View
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/work-orders/${wo.work_order_id}/edit`)}>
                              <Edit className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDuplicateClick(wo)}>
                              <Copy className="h-4 w-4 mr-2" /> Duplicate
                            </DropdownMenuItem>
                            {(wo.status === 'new_request' || wo.status === 'confirmed') && !wo.assigned_vendor_id && (
                              <>
                                <DropdownMenuItem onClick={() => handleQuickAssign(wo)}>
                                  <UserPlus className="h-4 w-4 mr-2" /> Quick Assign
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openBroadcastDialog(wo)}>
                                  <Send className="h-4 w-4 mr-2" /> Broadcast to Vendors
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Quick Assign Modal */}
      <QuickAssignModal
        open={quickAssignOpen}
        onOpenChange={setQuickAssignOpen}
        workOrder={selectedWorkOrder}
        onAssigned={() => fetchWorkOrders()}
      />

      {/* Duplicate Work Order Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-blue-600" />
              Duplicate Work Order
            </DialogTitle>
            <DialogDescription>
              Create a copy of {duplicateWorkOrder?.work_order_id} with a new date
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Original Work Order Info */}
            <div className="bg-muted p-3 rounded-lg text-sm">
              <div className="font-medium">{duplicateWorkOrder?.client_name}</div>
              <div className="text-muted-foreground">
                {duplicateWorkOrder?.target_language} • {duplicateWorkOrder?.work_order_type}
              </div>
              {duplicateWorkOrder?.interpreting_details?.service_location && (
                <div className="text-muted-foreground truncate">
                  📍 {duplicateWorkOrder.interpreting_details.service_location}
                </div>
              )}
            </div>
            
            {/* New Date */}
            <div className="space-y-2">
              <Label htmlFor="duplicate-date">New Date *</Label>
              <Input
                id="duplicate-date"
                type="date"
                value={duplicateDate}
                onChange={(e) => setDuplicateDate(e.target.value)}
                data-testid="duplicate-date-input"
              />
            </div>
            
            {/* New Time (for interpreting) */}
            {duplicateWorkOrder?.work_order_type === 'interpreting' && (
              <div className="space-y-2">
                <Label htmlFor="duplicate-time">Start Time</Label>
                <Input
                  id="duplicate-time"
                  type="time"
                  value={duplicateTime}
                  onChange={(e) => setDuplicateTime(e.target.value)}
                  data-testid="duplicate-time-input"
                />
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDuplicateSubmit} 
              disabled={duplicateLoading || !duplicateDate}
              data-testid="duplicate-submit-btn"
            >
              {duplicateLoading ? 'Creating...' : 'Create Duplicate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Broadcast Dialog */}
      <Dialog open={showBroadcast} onOpenChange={setShowBroadcast}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              Find Vendor for {broadcastWO?.work_order_id}
            </DialogTitle>
          </DialogHeader>
          
          {broadcastLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Languages:</strong> {broadcastWO?.languages?.join(', ')}
                </p>
                {broadcastWO?.service_category && (
                  <p className="text-sm text-blue-800">
                    <strong>Category:</strong> {broadcastWO.service_category}
                    {broadcastWO.service_subcategory && ` → ${broadcastWO.service_subcategory}`}
                  </p>
                )}
                <p className="text-sm text-blue-800">
                  <strong>Date:</strong> {formatDate(broadcastWO?.interpreting_details?.start_datetime)}
                </p>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="inviteAll" 
                    checked={inviteAll} 
                    onCheckedChange={setInviteAll}
                  />
                  <Label htmlFor="inviteAll">Invite all matching vendors ({matchingVendors.length})</Label>
                </div>
              </div>

              {!inviteAll && (
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {matchingVendors.length > 0 ? (
                    matchingVendors.map((vendor) => (
                      <div 
                        key={vendor.vendor_id} 
                        className={`flex items-center gap-3 p-3 border-b last:border-b-0 hover:bg-gray-50 ${vendor.is_recommended ? 'bg-green-50/50' : ''}`}
                      >
                        <Checkbox 
                          checked={selectedVendors.includes(vendor.vendor_id)}
                          onCheckedChange={() => toggleVendorSelection(vendor.vendor_id)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{vendor.name}</p>
                            {vendor.vendor_number && (
                              <span className="text-xs text-muted-foreground font-mono">{vendor.vendor_number}</span>
                            )}
                            {vendor.is_recommended && (
                              <Badge className="bg-green-100 text-green-800 text-xs">Recommended</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {vendor.languages?.join(', ')} • {vendor.email}
                          </p>
                          {vendor.match_reasons?.length > 0 && (
                            <p className="text-xs text-blue-600 mt-1">
                              {vendor.match_reasons.join(' • ')}
                            </p>
                          )}
                        </div>
                        <div className="text-right text-sm">
                          <p className="font-semibold text-blue-600">{vendor.match_score} pts</p>
                          <p className="text-green-600">{vendor.reliability_score}%</p>
                          <p className="text-muted-foreground">{vendor.total_assignments} jobs</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="p-8 text-center text-muted-foreground">
                      <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No matching vendors found</p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Additional Message (optional)</Label>
                <Textarea 
                  placeholder="Add any special instructions or notes for vendors..."
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                />
              </div>

              <div className="p-3 bg-orange-50 rounded-lg text-sm text-orange-800">
                <strong>Note:</strong> Vendors will receive both Email and SMS notifications. 
                First vendor to accept will be automatically assigned.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBroadcast(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBroadcast} 
              disabled={broadcastLoading || (!inviteAll && selectedVendors.length === 0)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="h-4 w-4 mr-2" />
              Send to {inviteAll ? matchingVendors.length : selectedVendors.length} Vendors
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrders;
